import { keyframes, styled } from "goober";
import {
  memo,
  type ReactNode,
  type RefObject,
  useEffect,
  useState,
} from "react";
import type { EdgeType, GraphEdge } from "./buildAccessModel";
import { edgeVisual } from "./edgeColor";
import { edgePath, inheritedEdgeSegments } from "./edgePath";
import { type NodePosition } from "./layoutGraph";

const OPACITY_BASE = 1;
const OPACITY_ACTIVE = 1;
const OPACITY_DIM = 0.1;
const DIM_BLUR = 2;

// -18 is a whole number of both dash periods (5+4 and 1+5), so the flow loops
// seamlessly. Mirrors the step map's connector animation.
const dashFlow = keyframes`
  to {
    stroke-dashoffset: -18;
  }
`;

const EdgePath = styled("path")`
  transition: opacity 160ms ease;
  &[data-dashed="true"] {
    animation: ${dashFlow} 1s linear infinite;
  }
`;

type Props = {
  edges: readonly GraphEdge[];
  positions: Map<string, NodePosition>;
  width: number;
  height: number;
  focusId: string | null;
  visibleSet: Set<string> | null;
  hideUnrelated: boolean;
  showDirect: boolean;
  showInherited: boolean;
  showAvailable: boolean;
  hoverLayer: EdgeType | null;
};

const edgeCategory = (type: EdgeType) =>
  type === "inherited"
    ? "inherited"
    : type === "derived"
      ? "derived"
      : "direct";

// An inherited edge is two hops (member → group → scope); dedupe its shared
// legs so a group draws one onward line and a member one dashed hop.
const segmentKey = (e: GraphEdge, type: EdgeType) =>
  type === "inherited"
    ? `inh1:${e.sourceId}->${e.viaGroupId ?? e.targetId}`
    : `inh2:${e.viaGroupId}->${e.targetId}`;

export const GraphEdges = memo(function GraphEdges({
  edges,
  positions,
  width,
  height,
  focusId,
  visibleSet,
  hideUnrelated,
  showDirect,
  showInherited,
  showAvailable,
  hoverLayer,
}: Props) {
  const dimmed: ReactNode[] = [];
  const sharp: ReactNode[] = [];
  const highlighted: ReactNode[] = [];
  const inheritedSeen = new Set<string>();

  for (const e of edges) {
    const source = positions.get(e.sourceId);
    const target = positions.get(e.targetId);
    if (!source || !target) continue;
    const active =
      focusId != null && (e.sourceId === focusId || e.targetId === focusId);
    const offMatch =
      visibleSet != null &&
      (!visibleSet.has(e.sourceId) || !visibleSet.has(e.targetId));
    // A search (matches but no focus) shows only the lines between two matched
    // cards; the rest are dropped so the matches read cleanly rather than a
    // faded web weaving behind every card. With a node focused, collapse
    // likewise drops the lines dangling to the hidden cards.
    if (focusId == null && offMatch) continue;
    if (hideUnrelated && !active && offMatch) continue;
    const category = edgeCategory(e.type);
    if (hoverLayer) {
      // Hovering a key isolates that line type — show only it, scoped to the
      // focused node's connections when one is selected (overriding the toggles).
      if (category !== hoverLayer) continue;
      if (e.type === "inherited" && !active) continue;
      if (focusId != null && !active) continue;
    } else {
      // Each line type is a toggleable layer. Solid (membership + direct grants)
      // and dotted (derived availability) draw whenever their layer is on;
      // inherited (dashed) only for the focused node — HoverEdges previews them
      // for the hovered one — so the view isn't swamped.
      if (category === "direct" && !showDirect) continue;
      if (e.type === "derived" && !showAvailable) continue;
      if (e.type === "inherited" && (!showInherited || !active)) continue;
    }
    const isDimmed = focusId != null && !active;
    const opacity = isDimmed
      ? hideUnrelated
        ? 0
        : OPACITY_DIM
      : focusId == null
        ? OPACITY_BASE
        : OPACITY_ACTIVE;

    // Inherited edges (always in-focus when drawn) route through the group they
    // flow via: a dashed hop to the group, then a solid leg for the group's own
    // grant onward. Shared legs draw once.
    if (e.type === "inherited") {
      const group = e.viaGroupId ? positions.get(e.viaGroupId) : undefined;
      for (const seg of inheritedEdgeSegments(source, group, target)) {
        const key = segmentKey(e, seg.type);
        if (inheritedSeen.has(key)) continue;
        inheritedSeen.add(key);
        const v = edgeVisual(seg.type, "active");
        highlighted.push(
          <EdgePath
            key={key}
            d={seg.d}
            fill="none"
            stroke={v.stroke}
            strokeWidth={1}
            strokeDasharray={v.dash}
            strokeLinecap="round"
            style={{ opacity }}
            data-dashed={v.dash ? "true" : undefined}
          />
        );
      }
      continue;
    }

    const { stroke, dash } = edgeVisual(e.type, active ? "active" : "rest");
    const el = (
      <EdgePath
        key={e.id}
        d={edgePath(source, target)}
        fill="none"
        stroke={stroke}
        strokeWidth={1}
        strokeDasharray={dash}
        strokeLinecap="round"
        style={{ opacity }}
        // Only the in-focus edges flow; dimmed ones stay static so the blurred
        // layer can be cached rather than re-blurred each frame.
        data-dashed={dash && !isDimmed ? "true" : undefined}
      />
    );
    if (isDimmed) dimmed.push(el);
    else if (active) highlighted.push(el);
    else sharp.push(el);
  }

  // Two layers: non-highlighted edges sit below the cards (z 0, under the dimmed
  // cards at z 1) so they don't clutter; highlighted edges sit above the dimmed
  // cards (z 2) and tuck under the relevant cards (z 3). Within the z-2 layer the
  // active edges paint last so they sit on top of the resting lines crossing
  // them (SVG has no per-path z-index — later siblings win).
  return (
    <>
      <svg
        width={width}
        height={height}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: 0,
          pointerEvents: "none",
          overflow: "visible",
        }}
      >
        <g style={{ filter: `blur(${DIM_BLUR}px)` }}>{dimmed}</g>
      </svg>
      <svg
        width={width}
        height={height}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: 2,
          pointerEvents: "none",
          overflow: "visible",
        }}
      >
        {sharp}
        {highlighted}
      </svg>
    </>
  );
});

type HoverProps = {
  edges: readonly GraphEdge[];
  positions: Map<string, NodePosition>;
  width: number;
  height: number;
  canvasRef: RefObject<HTMLDivElement | null>;
  active: boolean;
  visibleSet: Set<string> | null;
  hideUnrelated: boolean;
  showDirect: boolean;
  showInherited: boolean;
  showAvailable: boolean;
};

// Hovering a card previews its connections — the same ones a click reveals,
// drawn in the hover stroke on top of the resting lines. The preview owns its
// hover state and paints in its own svg so pointer movement redraws only the
// hovered node's edges, never the graph; its visibility rules must stay in
// sync with GraphEdges' rest-state ones above.
export function HoverEdges({
  edges,
  positions,
  width,
  height,
  canvasRef,
  active,
  visibleSet,
  hideUnrelated,
  showDirect,
  showInherited,
  showAvailable,
}: HoverProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // Listeners track even while inactive, so the preview is current the moment
  // focus or drag ends.
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const over = (e: MouseEvent) => {
      const card = (e.target as Element).closest("[data-node-id]");
      setHoveredId(card?.getAttribute("data-node-id") ?? null);
    };
    const leave = () => setHoveredId(null);
    el.addEventListener("mouseover", over);
    el.addEventListener("mouseleave", leave);
    return () => {
      el.removeEventListener("mouseover", over);
      el.removeEventListener("mouseleave", leave);
    };
  }, [canvasRef]);

  if (!active || !hoveredId) return null;

  const paths: ReactNode[] = [];
  const hoverSeen = new Set<string>();
  for (const e of edges) {
    if (e.sourceId !== hoveredId && e.targetId !== hoveredId) continue;
    if (
      hideUnrelated &&
      visibleSet &&
      (!visibleSet.has(e.sourceId) || !visibleSet.has(e.targetId))
    )
      continue;
    if (edgeCategory(e.type) === "direct" && !showDirect) continue;
    if (e.type === "derived" && !showAvailable) continue;
    if (e.type === "inherited" && !showInherited) continue;
    const source = positions.get(e.sourceId);
    const target = positions.get(e.targetId);
    if (!source || !target) continue;
    if (e.type === "inherited") {
      const group = e.viaGroupId ? positions.get(e.viaGroupId) : undefined;
      for (const seg of inheritedEdgeSegments(source, group, target)) {
        const key = segmentKey(e, seg.type);
        if (hoverSeen.has(key)) continue;
        hoverSeen.add(key);
        const v = edgeVisual(seg.type, "hover");
        paths.push(
          <EdgePath
            key={key}
            d={seg.d}
            fill="none"
            stroke={v.stroke}
            strokeWidth={1}
            strokeDasharray={v.dash}
            strokeLinecap="round"
            data-dashed={v.dash ? "true" : undefined}
          />
        );
      }
      continue;
    }
    const { stroke, dash } = edgeVisual(e.type, "hover");
    paths.push(
      <EdgePath
        key={e.id}
        d={edgePath(source, target)}
        fill="none"
        stroke={stroke}
        strokeWidth={1}
        strokeDasharray={dash}
        strokeLinecap="round"
        data-dashed={dash ? "true" : undefined}
      />
    );
  }
  if (paths.length === 0) return null;

  return (
    <svg
      width={width}
      height={height}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        zIndex: 2,
        pointerEvents: "none",
        overflow: "visible",
      }}
    >
      {paths}
    </svg>
  );
}
